import { forwardRef } from 'react';

import type { IncidentPriority } from '../../../types';
import type { BadgeProps } from './Badge';
import type { SeverityTone } from './types';

export interface PriorityBadgeProps extends Omit<BadgeProps, 'variant' | 'leadingDot' | 'leadingIcon'> {
  priority: IncidentPriority;
  showDot?: boolean;
  showLabel?: boolean;
  showLongLabel?: boolean;
}

interface PriorityPresentation {
  label: string;
  tone: SeverityTone;
  dotColor: string;
}

const presentationByPriority: Record<IncidentPriority, PriorityPresentation> = {
  P1: {
    label: 'P1',
    tone: 'critical',
    dotColor: 'bg-[color:var(--color-severity-critical-solid)]',
  },
  P2: {
    label: 'P2',
    tone: 'severity-high',
    dotColor: 'bg-[color:var(--color-severity-high-solid)]',
  },
  P3: {
    label: 'P3',
    tone: 'severity-medium',
    dotColor: 'bg-[color:var(--color-severity-medium-solid)]',
  },
  P4: {
    label: 'P4',
    tone: 'severity-low',
    dotColor: 'bg-[color:var(--color-severity-low-solid)]',
  },
};

const longLabels: Record<IncidentPriority, string> = {
  P1: 'Critical',
  P2: 'High',
  P3: 'Medium',
  P4: 'Low',
};

const severityToneClasses: Record<SeverityTone, string> = {
  critical:
    'bg-[color:var(--color-severity-critical-muted-bg)] text-[color:var(--color-severity-critical-muted-fg)] border-[color:var(--color-severity-critical-border)]',
  'severity-high':
    'bg-[color:var(--color-severity-high-muted-bg)] text-[color:var(--color-severity-high-muted-fg)] border-[color:var(--color-severity-high-border)]',
  'severity-medium':
    'bg-[color:var(--color-severity-medium-muted-bg)] text-[color:var(--color-severity-medium-muted-fg)] border-[color:var(--color-severity-medium-border)]',
  'severity-low':
    'bg-[color:var(--color-severity-low-muted-bg)] text-[color:var(--color-severity-low-muted-fg)] border-[color:var(--color-severity-low-border)]',
  info:
    'bg-[color:var(--color-severity-info-muted-bg)] text-[color:var(--color-severity-info-muted-fg)] border-[color:var(--color-severity-info-border)]',
};

const sizeClasses = {
  sm: {
    root: 'h-5 px-2 py-0.5 text-xs',
    dot: 'h-1.5 w-1.5',
  },
  md: {
    root: 'h-6 px-2.5 py-0.5 text-sm',
    dot: 'h-2 w-2',
  },
} as const;

function cn(...parts: Array<string | undefined | false | null>): string {
  return parts.filter(Boolean).join(' ');
}

export const PriorityBadge = forwardRef<HTMLSpanElement, PriorityBadgeProps>(function PriorityBadge(
  {
    priority,
    showDot = true,
    showLabel = true,
    showLongLabel = false,
    size = 'md',
    className,
    children,
    ...rest
  },
  ref,
) {
  const presentation = presentationByPriority[priority];
  const classes = sizeClasses[size];
  const defaultAriaLabel = showLongLabel
    ? `Priority ${priority.slice(1)} — ${longLabels[priority]}`
    : `Priority: ${presentation.label}`;

  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex max-w-full shrink-0 items-center gap-1.5 rounded-full border font-medium',
        classes.root,
        severityToneClasses[presentation.tone],
        className,
      )}
      aria-label={rest['aria-label'] ?? defaultAriaLabel}
      {...rest}
    >
      {showDot ? <span aria-hidden="true" className={cn('rounded-full', classes.dot, presentation.dotColor)} /> : null}
      {showLabel ? <span className="truncate">{children ?? presentation.label}</span> : null}
    </span>
  );
});
